import {getApolloClient} from 'graphql-client/ApolloClient';

import {GET_ANALYTICS_DASHBOARD_QUERY} from './graphql/GetAnalyticsDashboard';
import {
  getAnalyticsDashboardViewStateId,
  MAIN_ANALYTICS_DASHBOARD_VIEW_CONTEXT,
} from './view-state/AnalyticsDashboardViewState';

/**
 * Force the analytics dashboard details to refresh by issuing a network request for the details
 *
 * We found that we need to call this when an AnalyticsQuery (block) is added to the dashboard. We theorize
 * that because the update dashboard REST response includes some attributes of the new AnalyticsQuery (like
 * id and name) but not all (like visualization), the AnalyticsDashboardDetails component does not update. To
 * fix this, we force-refresh the query.
 *
 * TODO(BNCH-12369): Check whether Apollo 3 fixes this. Ideally we wouldn't need this function, because it
 * incurs extra network fetches after REST calls.
 */
export async function refreshDetails(analyticsDashboardId: string): Promise<void> {
  await getApolloClient().query({
    // Ideally we would use only a query that refreshed the local fields, but that doesn't seem to fix the bug
    // above. Instead it seems like we need to additionally refresh the local fields of our queries in
    // the query.
    //
    // So the next ideal, is a query that only refreshes the server dashboard and the local fields of the
    // query (and not the local fields of the dashboard that are unnecessary), but there is a bug with
    // apollo client:extract that makes it so queries using viewStateId can't pass the validation of queries
    // on a production environment. This requires the GetAnalyticsDashboard query to be whitelisted from that
    // validation, and it would require this new query to be whitelisted. Instead of whitelisting 2 queries
    // we simply choose to perform the whole query.
    //
    // TODO(BNCH-17318): Make a more minimal version of this query for refreshing dashboards
    query: GET_ANALYTICS_DASHBOARD_QUERY,
    variables: {
      analyticsDashboardId,
      // Refresh details is only called in a context where the user has write access to the dashboard, and
      // the only place where that is true is when the user is in the MAIN_ANALYTICS_DASHBOARD and not any
      // other views. We use that assumption to always generate viewStateIds for the MAIN_ANALYTICS_DASHBOARD
      // instead of needing to take viewStateId as a parameter because there's no other way of knowing the
      // current ViewContext.
      viewStateId: getAnalyticsDashboardViewStateId(
        analyticsDashboardId,
        MAIN_ANALYTICS_DASHBOARD_VIEW_CONTEXT
      ),
    },
    fetchPolicy: 'network-only',
  });
}
